import type {
  AnyRoute,
  RouteChildren,
  ParamsRecord,
  SearchRecord,
  RoutesById,
  RoutesByToPath,
  RoutePaths,
  NavigateOptionsByToPath,
} from './types'

export const collectRoutes = (
  route: AnyRoute,
  acc: AnyRoute[] = [],
): AnyRoute[] => {
  acc.push(route)
  const children = route.children as RouteChildren
  for (const child of children) {
    collectRoutes(child, acc)
  }
  return acc
}

export const hasIndexChildRuntime = (route: AnyRoute): boolean => {
  const children = route.children as RouteChildren
  for (const child of children) {
    if (child.types.path === '/' || hasIndexChildRuntime(child)) {
      return true
    }
  }
  return false
}

function compareRouteMatchRank(a: string, b: string): number {
  const aSegments = splitPathSegments(a)
  const bSegments = splitPathSegments(b)
  const maxLength = Math.max(aSegments.length, bSegments.length)

  for (let index = 0; index < maxLength; index++) {
    const aScore = routeSegmentScore(aSegments[index])
    const bScore = routeSegmentScore(bSegments[index])
    if (aScore !== bScore) {
      return bScore - aScore
    }
  }

  return bSegments.length - aSegments.length
}

function routeSegmentScore(segment: string | undefined): number {
  if (segment === undefined) return 0
  if (segment === '$') return 1
  if (segment.startsWith('$')) return 2
  return 3
}

export const buildRouterMetadata = <TRouteTree extends AnyRoute>(
  routeTree: TRouteTree,
) => {
  const routes = collectRoutes(routeTree, [])
  const routesById: Record<string, AnyRoute> = Object.create(null)
  const routesByToPath: Record<string, AnyRoute> = Object.create(null)
  const routePaths: string[] = []
  for (const route of routes) {
    routesById[route.id] = route
    if (!hasIndexChildRuntime(route)) {
      routesByToPath[route.to] = route
      routePaths.push(route.to)
    }
  }
  // Ranked once per table (TanStack Router ranks its route tree at build), so
  // a match only walks the ranked list. `toSorted` is stable: equal ranks keep
  // declaration order.
  const rankedRoutes: ReadonlyArray<readonly [string, AnyRoute]> = Object.entries(
    routesByToPath,
  ).toSorted((a, b) => compareRouteMatchRank(a[0], b[0]))
  const navigateOptionsByToPath: Record<string, unknown> = Object.create(null)
  for (const path of routePaths) {
    navigateOptionsByToPath[path] = undefined
  }
  return {
    routesById: routesById as RoutesById<TRouteTree>,
    routesByToPath: routesByToPath as RoutesByToPath<TRouteTree>,
    routePaths: routePaths as unknown as RoutePaths<TRouteTree>,
    rankedRoutes,
    navigateOptionsByToPath:
      navigateOptionsByToPath as NavigateOptionsByToPath<TRouteTree>,
  }
}

export const normalizePathRuntime = (path?: string) => {
  if (!path || path === '/') return ''
  return path.replaceAll(/^\/+/g, '').replaceAll(/\/+$/g, '')
}

export const resolveFullPathRuntime = (
  parentFullPath: string,
  path?: string,
) => {
  const normalized = normalizePathRuntime(path)
  if (!normalized) {
    return parentFullPath
  }
  return parentFullPath === '/'
    ? `/${normalized}`
    : `${parentFullPath}/${normalized}`
}

export const resolveRouteIdRuntime = (path?: string, id?: string) => {
  if (id) return id
  if (!path || path === '/') {
    return '__index__'
  }
  const normalized = normalizePathRuntime(path)
  if (!normalized) {
    throw new Error('Route without path must provide an id')
  }
  return normalized.replaceAll('/', '__')
}

export const fillParams = (to: string, params?: ParamsRecord) => {
  if (!params) return to.replaceAll('$', '')
  let result = to.replaceAll(/\$([A-Za-z0-9_]+)/g, (_, key: string) => {
    if (!(key in params)) {
      throw new Error(`Missing param ${key}`)
    }
    return encodeURIComponent(params[key])
  })
  if (result.includes('$')) {
    if (!('_splat' in params)) {
      throw new Error('Missing param _splat')
    }
    const splat = params._splat
      .split('/')
      .map((segment) => encodeURIComponent(segment))
      .join('/')
    result = result.replaceAll('$', splat)
  }
  return result.replaceAll(/\/+/g, '/')
}

export const buildSearch = (search?: SearchRecord) => {
  if (!search) return ''
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === null) continue
    if (Array.isArray(value)) {
      for (const entry of value) {
        params.append(key, String(entry))
      }
    } else {
      // oxlint-disable-next-line typescript/no-base-to-string -- SearchRecord value is typed as primitive | array; objects not expected at runtime
      params.set(key, String(value))
    }
  }
  const query = params.toString()
  return query ? `?${query}` : ''
}

export const parseSearchParams = (search: string): SearchRecord => {
  const result: SearchRecord = {}
  if (!search) {
    return result
  }
  const params = new URLSearchParams(
    search.startsWith('?') ? search : `?${search}`,
  )
  for (const [key, value] of params.entries()) {
    if (key in result) {
      const existing = result[key]
      if (Array.isArray(existing)) {
        existing.push(value)
      } else {
        result[key] = [existing, value]
      }
    } else {
      result[key] = value
    }
  }
  return result
}

export const splitPathSegments = (path: string): string[] => {
  if (!path || path === '/') return []
  const trimmed = path.replaceAll(/^\/+|\/+$/g, '')
  return trimmed ? trimmed.split('/') : []
}

export const matchPathnameRuntime = (pattern: string, pathname: string) => {
  const patternSegments = splitPathSegments(pattern)
  const pathnameSegments = splitPathSegments(pathname)
  const params: ParamsRecord = {}
  // An empty pattern is `/` and matches only `/`: matching it as a prefix let a `/` index route swallow every
  // unknown path, so the root's notFoundComponent never rendered.
  if (patternSegments.length === 0) {
    return pathnameSegments.length === 0 ? params : null
  }
  let i = 0
  for (; i < patternSegments.length; i++) {
    const patternSegment = patternSegments[i]
    const lastPatternIndex = patternSegments.length - 1
    const lastSegment = i === lastPatternIndex
    if (patternSegment === '$') {
      params._splat = pathnameSegments
        .slice(i)
        .map((segment) => decodeURIComponent(segment))
        .join('/')
      return params
    }
    const pathnameSegment = pathnameSegments[i]
    if (pathnameSegment === undefined) {
      return null
    }
    if (patternSegment.startsWith('$')) {
      const key = patternSegment.slice(1)
      if (!key) {
        return null
      }
      params[key] = decodeURIComponent(pathnameSegment)
    } else if (patternSegment !== pathnameSegment) {
      return null
    }
    if (lastSegment && pathnameSegments.length > patternSegments.length) {
      return null
    }
  }
  if (pathnameSegments.length !== patternSegments.length) {
    return null
  }
  return params
}

export const getRouteChain = (route: AnyRoute): AnyRoute[] => {
  const chain: AnyRoute[] = []
  let current: AnyRoute | null = route
  while (current) {
    chain.push(current)
    current = current.parentRoute
  }
  return chain.toReversed()
}
